import type { Address, Team, TeamClaim } from '@baseball-near-you/prisma';
import { Button, cn } from '@baseball-near-you/ui';
import { PATHS } from '@bb/lib/constants';
import { getInitials } from '@bb/lib/utils/getInitials';
import { Verified } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { FallbackNextImage } from '../FallbackNextImage';
import { TeamLocation } from '../TeamHeader';

export const TeamAvatarSizes = {
  sm: 'size-14 min-h-[3.5rem] min-w-[3.5rem]',
  md: 'size-20 min-h-[5rem] min-w-[5rem]',
  lg: 'size-28 min-h-[7rem] min-w-[7rem]',
};

export function TeamAvatar({
  teamName,
  logoUrl,
  size = 'sm',
}: {
  teamName: string;
  logoUrl: string | null;
  size?: keyof typeof TeamAvatarSizes;
}) {
  const initials = getInitials(teamName);
  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gray-100',
        TeamAvatarSizes[size]
      )}
    >
      <FallbackNextImage
        fallback={
          <div
            className={cn(
              'title flex h-full w-full items-center justify-center text-2xl text-gray-700',
              initials.length > 2 && 'text-xl',
              initials.length > 3 && 'text-lg',
              initials.length > 4 && 'text-base',
              initials.length > 5 && 'text-sm'
            )}
          >
            {initials}
          </div>
        }
        alt={`${teamName} logo`}
        src={logoUrl}
        fill
        className="object-contain"
        sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 10vw"
      />
    </div>
  );
}

export function LeagueMapTeam({
  hideActions = false,
  isHighlighted = false,
  ...props
}: {
  team: Team & { distance?: number; address: Address[]; claims?: TeamClaim[] };
  setCurrentTeam: (
    team: Team & { distance?: number; address: Address[] }
  ) => void;
  isCurrentTeam: boolean;
  hideActions?: boolean;
  markerPreview?: boolean;
  isHighlighted?: boolean;
}) {
  const teamRef = useRef<HTMLButtonElement | null>(null);
  const pathname = usePathname();

  const posthog = usePostHog();

  useEffect(() => {
    if (props.isCurrentTeam) {
      teamRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [props.isCurrentTeam]);

  return (
    <button
      type="button"
      ref={teamRef}
      className={cn(
        'w-full cursor-pointer rounded-r-md rounded-tl-xl rounded-bl-md border p-2 transition-all transition-colors',
        props.markerPreview
          ? 'hover:border-gray-300 hover:shadow-sm'
          : 'border-0',
        props.isCurrentTeam && 'rounded-tl-3xl bg-red/10',
        isHighlighted && 'border-red!'
      )}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          props.setCurrentTeam(props.team);
        }
      }}
      onClick={() => props.setCurrentTeam(props.team)}
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-start space-x-3">
          <TeamAvatar teamName={props.team.name} logoUrl={props.team.logoUrl} />

          <div className="flex w-full flex-col items-start justify-center">
            {props.team.address[0] && (
              <div className="mb-1 flex w-full items-center justify-between">
                <TeamLocation
                  pinIconSize={18}
                  address={props.team.address[0]}
                  className="text-sm"
                />
                {/* {!pathname.includes('us') &&
                  !!(props.team.address[0] as any).distance &&
                  !props.markerPreview && (
                    <p className="mt-2 font-medium text-red text-sm">
                      {(props.team.address[0] as any).distance <
                      METERS_IN_MILE / 4
                        ? `${metersToFeet(
                            (props.team.address[0] as any).distance
                          ).toFixed(2)} ft.`
                        : `${metersToMiles(
                            (props.team.address[0] as any).distance
                          )?.toFixed(2)} mi.`}
                    </p>
                  )} */}
              </div>
            )}
            <div className="flex items-center justify-start gap-x-0.75">
              <h2 className="title pl-1 text-left text-black text-lg!">
                {props.team.name}
              </h2>
              {/* TODO need to figure out how to get claims included on the /nearby route */}
              {!!props.team.claims && props.team.claims.length > 0 && (
                <Verified className="fill-red stroke-white pb-0.5" size={20} />
              )}
            </div>
          </div>
        </div>
        {!hideActions && (
          <div className="mt-2 flex w-full space-x-2">
            <Link
              onClick={(e) => {
                posthog.capture('team_map_view_team', {
                  team_slug: props.team.slug,
                  location: `${props.markerPreview ? 'map_marker_' : ''}view_team_button`,
                });
                e.stopPropagation();
              }}
              className="w-full"
              href={`${PATHS.Teams}/${props.team.slug}`}
              prefetch={false}
            >
              <Button
                className={
                  '!py-1.5 title h-min w-full whitespace-nowrap bg-red font-bold italic hover:bg-red/90'
                }
              >
                VIEW TEAM
              </Button>
            </Link>
            <Link
              onClick={(e) => {
                posthog.capture('team_map_view_team', {
                  team_slug: props.team.slug,
                  location: `${props.markerPreview ? 'map_marker_' : ''}reviews_button`,
                });
                e.stopPropagation();
              }}
              className="w-full"
              href={`${PATHS.Teams}/${props.team.slug}#reviews-section`}
              prefetch={false}
            >
              <Button
                className={
                  '!py-1.5 title h-min w-full bg-[#08253B] font-bold italic'
                }
              >
                REVIEWS
              </Button>
            </Link>
          </div>
        )}
      </div>
    </button>
  );
}
